/**
 * The OAuth providers we support in our OAuth product.
 * Currently we support `Amazon`, `Apple`, `Bitbucket`, `Discord`, `Facebook`, `Figma`, `Google`, `GitLab`,
 * `LinkedIn`, `Microsoft`, `Salesforce`, `Slack`, `Snapchat`, `TikTok`, `Twitch`, `Twitter`, and `Yahoo`.
 */ const OAuthProviders = {
    Google: 'google',
    Microsoft: 'microsoft',
    Apple: 'apple',
    Github: 'github',
    GitLab: 'gitlab',
    Facebook: 'facebook',
    Discord: 'discord',
    Salesforce: 'salesforce',
    Slack: 'slack',
    Amazon: 'amazon',
    Bitbucket: 'bitbucket',
    LinkedIn: 'linkedin',
    Coinbase: 'coinbase',
    Twitch: 'twitch',
    Twitter: 'twitter',
    TikTok: 'tiktok',
    Snapchat: 'snapchat',
    Figma: 'figma',
    Yahoo: 'yahoo'
};
/**
 * The Crypto Wallets we support in our crypto product.
 * Currently we support all ethereum and solana wallets.
 * We additionally detect and show popular wallets as distinct options.
 * The list of poular wallets include `Binance`, `Coinbase`, `Metamask`, and `Phantom`
 */ const Wallets = {
    Phantom: 'Phantom',
    Metamask: 'Metamask',
    Coinbase: 'Coinbase',
    Binance: 'Binance',
    GenericEthereumWallet: 'GenericEthereumWallet',
    GenericSolanaWallet: 'GenericSolanaWallet'
};
/**
 * Supported behaviors for positioning Google One Tap. The actual behavior
 * depends on browser support and Google's One Tap implementation.
 */ const OneTapPositions = {
    /**
   * Display Google One Tap using a native browser prompt if available, or
   * embedded in the existing SDK login form otherwise.
   * @deprecated This option has been renamed to `floatingOrEmbedded`
   */ embedded: 'embedded',
    /**
   * Display the One Tap prompt using a native browser prompt if available, or
   * in the top right corner otherwise. This is the default option.
   */ floating: 'floating',
    /**
   * Display the One Tap prompt embedded in the existing SDK login form if a
   * native browser prompt is not available, or not at all otherwise. This
   * option is not recommended for new applications.
   */ embeddedOnly: 'embeddedOnly',
    /**
   * Display the One Tap prompt using a native browser prompt if available, or
   * embedded in the existing SDK login form otherwise.
   */ floatingOrEmbedded: 'floatingOrEmbedded',
    /**
   * Attempt to display the One Tap prompt embedded in the existing SDK login
   * form, even if a native browser prompt is supported. This option is not
   * recommended. It disables native browser FedCM support even where it is
   * available, and will stop being honored by Google in the future.
   */ forceLegacyEmbedded: 'forceLegacyEmbedded'
};
/**
 * The methods array allows you to specify the authentication methods that you would like to expose
 * to your users. The order of the products that you include here will also be the order in which
 * they appear in the login form, with the first product specified appearing at the top of the login
 * form. We currently support passcodes on `email`, `sms` and `whatsapp`
 */ const OTPMethods = {
    SMS: 'sms',
    WhatsApp: 'whatsapp',
    Email: 'email'
};
var StytchEventType = /*#__PURE__*/ function(StytchEventType) {
    StytchEventType["MagicLinkLoginOrCreateEvent"] = "MAGIC_LINK_LOGIN_OR_CREATE";
    StytchEventType["OTPsLoginOrCreateEvent"] = "OTP_LOGIN_OR_CREATE";
    StytchEventType["OTPsAuthenticate"] = "OTP_AUTHENTICATE";
    StytchEventType["CryptoWalletAuthenticateStart"] = "CRYPTO_WALLET_AUTHENTICATE_START";
    StytchEventType["CryptoWalletAuthenticate"] = "CRYPTO_WALLET_AUTHENTICATE";
    StytchEventType["PasswordCreate"] = "PASSWORD_CREATE";
    StytchEventType["PasswordAuthenticate"] = "PASSWORD_AUTHENTICATE";
    StytchEventType["PasswordResetByEmailStart"] = "PASSWORD_RESET_BY_EMAIL_START";
    StytchEventType["PasswordResetByEmail"] = "PASSWORD_RESET_BY_EMAIL";
    StytchEventType["PasskeyRegister"] = "PASSKEY_REGISTER";
    StytchEventType["PasskeyAuthenticate"] = "PASSKEY_AUTHENTICATE";
    StytchEventType["PasskeySkip"] = "PASSKEY_SKIP";
    StytchEventType["PasskeyDone"] = "PASSKEY_DONE";
    /**
   * The authentication UI flow has completed successfully, including any steps
   * that take place after obtaining a valid session (such as saving recovery
   * codes).
   */ StytchEventType["AuthenticateFlowComplete"] = "AUTHENTICATE_FLOW_COMPLETE";
    /**
   * An OAuth Authorization flow has been initiated by a Connected Application.
   * The end-user may be prompted for consent to continue depending on the application
   * that is requesting access.
   */ StytchEventType["OAuthAuthorizeFlowStart"] = "OAUTH_AUTHORIZE_FLOW_START";
    /**
   * The end-user has completed the Authorization flow and is about to be redirected
   * back to the Connected Application
   */ StytchEventType["OAuthAuthorizeFlowComplete"] = "OAUTH_AUTHORIZE_FLOW_COMPLETE";
    /**
   * The end-user has denied the Authorization flow and is about to be redirected
   * back to the Connected Application with an error message
   */ StytchEventType["OAuthAuthorizeFlowConsentDenied"] = "OAUTH_AUTHORIZE_FLOW_CONSENT_DENIED";
    // More Events will go here ...
    // B2B Events
    StytchEventType["B2BMagicLinkEmailLoginOrSignup"] = "B2B_MAGIC_LINK_EMAIL_LOGIN_OR_SIGNUP";
    StytchEventType["B2BMagicLinkAuthenticate"] = "B2B_MAGIC_LINK_AUTHENTICATE";
    StytchEventType["B2BMagicLinkEmailDiscoverySend"] = "B2B_MAGIC_LINK_EMAIL_DISCOVERY_SEND";
    StytchEventType["B2BMagicLinkDiscoveryAuthenticate"] = "B2B_MAGIC_LINK_DISCOVERY_AUTHENTICATE";
    StytchEventType["B2BSSOStart"] = "B2B_SSO_START";
    StytchEventType["B2BSSOAuthenticate"] = "B2B_SSO_AUTHENTICATE";
    StytchEventType["B2BSSODiscoverConnections"] = "B2B_SSO_DISCOVER_CONNECTIONS";
    StytchEventType["B2BOAuthAuthenticate"] = "B2B_OAUTH_AUTHENTICATE";
    StytchEventType["B2BOAuthDiscoveryAuthenticate"] = "B2B_OAUTH_DISCOVERY_AUTHENTICATE";
    StytchEventType["B2BDiscoveryOrganizationsCreate"] = "B2B_DISCOVERY_ORGANIZATIONS_CREATE";
    StytchEventType["B2BDiscoveryIntermediateSessionExchange"] = "B2B_DISCOVERY_INTERMEDIATE_SESSION_EXCHANGE";
    StytchEventType["B2BPasswordAuthenticate"] = "B2B_PASSWORD_AUTHENTICATE";
    StytchEventType["B2BPasswordDiscoveryAuthenticate"] = "B2B_PASSWORD_DISCOVERY_AUTHENTICATE";
    StytchEventType["B2BPasswordResetByEmailStart"] = "B2B_PASSWORD_RESET_BY_EMAIL_START";
    StytchEventType["B2BPasswordResetByEmail"] = "B2B_PASSWORD_RESET_BY_EMAIL";
    StytchEventType["B2BPasswordResetBySession"] = "B2B_PASSWORD_RESET_BY_SESSION";
    StytchEventType["B2BSMSOTPSend"] = "B2B_SMS_OTP_SEND";
    StytchEventType["B2BSMSOTPAuthenticate"] = "B2B_SMS_OTP_AUTHENTICATE";
    StytchEventType["B2BTOTPCreate"] = "B2B_TOTP_CREATE";
    StytchEventType["B2BTOTPAuthenticate"] = "B2B_TOTP_AUTHENTICATE";
    StytchEventType["B2BRecoveryCodesRecover"] = "B2B_RECOVERY_CODES_RECOVER";
    StytchEventType["B2BPasswordDiscoveryResetStart"] = "B2B_PASSWORD_DISCOVERY_RESET_BY_EMAIL_START";
    StytchEventType["B2BDiscoveryPasswordReset"] = "B2B_PASSWORD_DISCOVERY_RESET_BY_EMAIL";
    StytchEventType["B2BImpersonationAuthenticate"] = "B2B_IMPERSONATION_AUTHENTICATE";
    StytchEventType["B2BOTPsEmailAuthenticate"] = "B2B_OTPS_EMAIL_AUTHENTICATE";
    StytchEventType["B2BOTPsEmailDiscoveryAuthenticate"] = "B2B_OTPS_EMAIL_DISCOVERY_AUTHENTICATE";
    StytchEventType["B2BOTPsEmailDiscoverySend"] = "B2B_OTPS_EMAIL_DISCOVERY_SEND";
    StytchEventType["B2BOTPsEmailLoginOrSignup"] = "B2B_OTPS_EMAIL_LOGIN_OR_SIGNUP";
    StytchEventType["B2BOrganizationsGetBySlug"] = "B2B_ORGANIZATIONS_GET_BY_SLUG";
    return StytchEventType;
}({});
var RNUIProducts = /*#__PURE__*/ function(RNUIProducts) {
    RNUIProducts[RNUIProducts["emailMagicLinks"] = 0] = "emailMagicLinks";
    RNUIProducts[RNUIProducts["oauth"] = 1] = "oauth";
    RNUIProducts[RNUIProducts["otp"] = 2] = "otp";
    RNUIProducts[RNUIProducts["passwords"] = 3] = "passwords";
    return RNUIProducts;
}({});

export { OAuthProviders, OTPMethods, OneTapPositions, RNUIProducts, StytchEventType, Wallets };
//# sourceMappingURL=ui.mjs.map
